import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { Button } from 'reactstrap'
import AppPermissionsSwitcher from '../../../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../../../Components/AppPermissionsSwitcher/constants/actions'
import { setDeleteCallback } from '../../../../Components/Modals/DeleteModal/utils'
import TableImage from '../../../../Components/TanstackTable/components/TableImage'
import projects from '../../../../api/projects/hooks'
import { MODAL_TYPES } from '../../../../common/constants'
import { projectResponseFields } from '../../../../common/response/project'
import { showModal } from '../../../../store/modal'
import { findEntityTitle, formatDateTime } from '../../../../utils/general'
import columnWidths from './columnWidths'

const getProjectQuickViewData = (project) => {
    return {
        id: project[projectResponseFields.ID],
        internalAppId: project[projectResponseFields.INTERNAL_APP_ID],
        projectStatus: project[projectResponseFields.PROJECT_STATUS],
        projectTitle: project[projectResponseFields.PROJECT_TITLE],
        city: project[projectResponseFields.CITY],
        widerArea: project[projectResponseFields.WIDER_AREA],
        quarter: project[projectResponseFields.QUARTER],
        street: project[projectResponseFields.STREET],
        streetNumber: project[projectResponseFields.STREET_NUMBER],
        agent: project[projectResponseFields.AGENT],
        projectEndDate: project[projectResponseFields.PROJECT_END_DATE],
        mainPhoto: project.mainPhoto
            ? {
                  id: project.mainPhoto.id,
                  updated: project.mainPhoto.updated,
              }
            : undefined,
        translations: project.translations,
    }
}

export const useTableColumns = (module) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const currentLang = localStorage.getItem('i18nextLng')
    const { mutate: deleteProject } = projects.DeleteProject.useMutation()

    const handleQuickViewModal = useCallback(
        (data) => {
            const filteredData = getProjectQuickViewData(data)
            const payload = {
                modalType: MODAL_TYPES.QUICK_VIEW_MODAL,
                modalProps: {
                    data: filteredData,
                    appModule: 'project',
                },
            }

            dispatch(showModal(payload))
        },
        [dispatch]
    )

    const handleDelete = useCallback(
        (id) => {
            const callbackId = 'delete-project'
            const payload = {
                modalType: MODAL_TYPES.DELETE_MODAL,
                modalProps: { callbackId, id },
            }

            setDeleteCallback(callbackId, () => deleteProject({ id }))
            dispatch(showModal(payload))
        },
        [dispatch, deleteProject]
    )

    const columns = useMemo(() => {
        const w = { ...columnWidths.hr, ...(columnWidths[currentLang] ?? {}) }

        return [
            {
                header: t('app.common.id'),
                id: projectResponseFields.INTERNAL_APP_ID,
                accessorKey: projectResponseFields.INTERNAL_APP_ID,
                enableSorting: true,
                size: w.internalAppId,
                meta: { exportId: projectResponseFields.INTERNAL_APP_ID },
            },
            {
                header: t('table.header.common.mainPhoto'),
                id: 'mainPhoto',
                enableSorting: false,
                size: w.mainPhoto,
                accessorFn: (project) => project.mainPhoto ?? null,
                cell: ({ getValue, row }) => {
                    const mainPhoto = getValue()

                    if (!mainPhoto) return null

                    return (
                        <AppPermissionsSwitcher
                            permission="AllowedPhotos"
                            photo={mainPhoto}
                            module={module}
                            itemAgentId={row.original[projectResponseFields.AGENT]?.[0]?.id}
                            itemAgentOfficeId={row.original[projectResponseFields.AGENT]?.[0]?.agencyOffice?.id}
                        >
                            {({ photo }) => photo ? <TableImage photoId={photo.id} updated={photo.updated} alt={photo.fileDescription} /> : null}
                        </AppPermissionsSwitcher>
                    )
                },
            },
            {
                header: t('app.common.status'),
                id: projectResponseFields.PROJECT_STATUS,
                accessorFn: (row) => row[projectResponseFields.PROJECT_STATUS]?.name ?? null,
                enableSorting: true,
                size: w.projectStatus,
                meta: { exportId: `${projectResponseFields.PROJECT_STATUS}_name` },
            },
            {
                header: t('app.common.project.title'),
                id: projectResponseFields.PROJECT_TITLE,
                enableSorting: false,
                size: w.projectTitle,
                meta: { exportId: `translations_${currentLang}_projectTitle` },
                accessorFn: (project) => findEntityTitle(project, projectResponseFields.PROJECT_TITLE),
                cell: (cellContext) => cellContext.row.original?.translations[currentLang]?.projectTitle,
            },
            {
                header: t('app.common.cityMunicipality'),
                id: projectResponseFields.CITY,
                accessorFn: (row) => row[projectResponseFields.CITY]?.name ?? null,
                enableSorting: true,
                size: w.city,
                meta: { exportId: `${projectResponseFields.CITY}_name` },
            },
            {
                header: t('app.common.widerArea'),
                id: projectResponseFields.WIDER_AREA,
                enableSorting: true,
                size: w.widerArea,
                meta: { exportId: `${projectResponseFields.WIDER_AREA}_name` },
                accessorFn: (row) => row[projectResponseFields.WIDER_AREA]?.name ?? null,
                cell: (cellContext) => (
                    <AppPermissionsSwitcher
                        permission="TableCell"
                        permissionToken="view.project.widerArea"
                        itemAgentId={cellContext.row.original[projectResponseFields.AGENT]?.[0]?.id}
                        itemAgentOfficeId={cellContext.row.original[projectResponseFields.AGENT]?.[0]?.agencyOffice?.id}
                    >
                        {({ hasPermission }) => hasPermission ? cellContext.getValue() : null}
                    </AppPermissionsSwitcher>
                ),
            },
            {
                header: t('app.common.quarter'),
                id: projectResponseFields.QUARTER,
                enableSorting: true,
                size: w.quarter,
                meta: { exportId: `${projectResponseFields.QUARTER}_name` },
                accessorFn: (row) => row[projectResponseFields.QUARTER]?.name ?? null,
                cell: (cellContext) => (
                    <AppPermissionsSwitcher
                        permission="TableCell"
                        permissionToken="view.project.quarter"
                        itemAgentId={cellContext.row.original[projectResponseFields.AGENT]?.[0]?.id}
                        itemAgentOfficeId={cellContext.row.original[projectResponseFields.AGENT]?.[0]?.agencyOffice?.id}
                    >
                        {({ hasPermission }) => hasPermission ? cellContext.getValue() : null}
                    </AppPermissionsSwitcher>
                ),
            },
            {
                header: t('app.common.agent'),
                id: projectResponseFields.AGENT,
                accessorKey: projectResponseFields.AGENT,
                enableSorting: true,
                size: w.agent,
                meta: { fieldName: projectResponseFields.AGENT, exportId: ['agent_0_firstName', 'agent_0_lastName'] },
                cell: ({ row }) => (
                    <>
                        {row.original?.[projectResponseFields.AGENT]?.[0]?.firstName} {row.original?.[projectResponseFields.AGENT]?.[0]?.lastName}
                    </>
                ),
            },
            {
                header: t('app.common.contractDate'),
                id: projectResponseFields.CONTRACT_DATE,
                accessorKey: projectResponseFields.CONTRACT_DATE,
                enableSorting: true,
                size: w.contractDate,
                meta: { exportId: projectResponseFields.CONTRACT_DATE },
                cell: ({ row }) => formatDateTime(row.original?.[projectResponseFields.CONTRACT_DATE]),
            },
            {
                header: t('app.common.contractNumber'),
                id: projectResponseFields.CONTRACT_NO,
                accessorKey: projectResponseFields.CONTRACT_NO,
                enableSorting: false,
                size: w.contractNo,
                meta: { exportId: projectResponseFields.CONTRACT_NO },
            },
            {
                header: t('app.common.project.type'),
                id: projectResponseFields.PROJECT_TYPE,
                accessorFn: (row) => row[projectResponseFields.PROJECT_TYPE]?.name ?? null,
                enableSorting: true,
                size: w.projectType,
                meta: { exportId: `${projectResponseFields.PROJECT_TYPE}_name` },
            },
            {
                header: t('app.common.project.endDate'),
                id: projectResponseFields.PROJECT_END_DATE,
                accessorKey: projectResponseFields.PROJECT_END_DATE,
                enableSorting: true,
                size: w.projectEndDate,
                meta: { exportId: projectResponseFields.PROJECT_END_DATE },
                cell: ({ row }) => formatDateTime(row.original?.[projectResponseFields.PROJECT_END_DATE]),
            },
            {
                id: 'actions',
                enableResizing: false,
                meta: { thDivClass: 'text-center', stopPropagation: true },
                size: w.actions,
                cell: ({ row }) => {
                    const itemAgentId = row.original.agent?.[0]?.id

                    return (
                        <ul className="list-inline hstack gap-2 mb-0 justify-content-center">
                            <li className="list-inline-item">
                                <Link
                                    to="#"
                                    title={t('button.view')}
                                    className="btn text-warning d-inline-block p-0"
                                    onClick={() => handleQuickViewModal(row.original)}
                                >
                                    <i className="mdi mdi-eye-outline fs-16"></i>
                                </Link>
                            </li>

                            <AppPermissionsSwitcher permission="ModulePermissions" itemAgentId={itemAgentId} module={module} action={action.edit}>
                                {({ hasModulePermission }) =>
                                    hasModulePermission && (
                                        <>
                                            <li className="list-inline-item edit">
                                                <Link
                                                    title={t('button.edit')}
                                                    to={`/projects/${row.original.id}/edit`}
                                                    className="text-info d-inline-block edit-item-btn"
                                                >
                                                    <i className="mdi mdi-pencil-outline fs-16"></i>
                                                </Link>
                                            </li>
                                            <li className="list-inline-item edit">
                                                <Button
                                                    type="button"
                                                    color="link"
                                                    title={t('button.delete')}
                                                    className="text-danger d-inline-block remove-item-btn p-0"
                                                    onClick={() => handleDelete(row.original.id)}
                                                >
                                                    <i className="mdi mdi-trash-can-outline fs-16"></i>
                                                </Button>
                                            </li>
                                        </>
                                    )
                                }
                            </AppPermissionsSwitcher>
                        </ul>
                    )
                },
            },
        ]
    }, [currentLang, module, handleQuickViewModal, handleDelete, t])

    return columns
}
